// Globals
import { type PromptProps } from '@ant-design/x';
import { Button, GetProps } from 'antd';
import { useMemo } from 'react';

// Modules
import { useSocket } from '@/modules/Socket';

// Components
import { ChatCircleIcon, CloseOutlineIcon } from '@/icons';
import { Message } from '../../components';
import { SenderContainer } from '../SenderContainer/SenderContainer';
import { UserDataContainer } from '../UserDataContainer/UserDataContainer';
import {
  StyledBlock,
  StyledBubbleList,
  StyledContainer,
  StyledInfo,
  StyledName,
  StyledPrompts,
  StyledTime,
  StyledWrapper
} from './ChatContainerStyles';

// Helpers
import { formatDate, formatTime } from '@/utils';
import { renderMessage } from '../../helpers';

type TChatContainerWrapperProps = {
  onClose: () => void;
}

type BubbleDataType = NonNullable<GetProps<typeof StyledBubbleList>['items']>[number];

const ChatContainerWrapper = ({ onClose }: TChatContainerWrapperProps) => {
  const {
    sendMessage,
    messages,
    buttons,
    isTyping,
    isBot,
    shareContacts,
    userInfo,
    isSharingContacts,
    setUser,
    ref,
    onSendRate,
    onCancelShareContacts,
  } = useSocket();

  const handlePressButton = async (value: { data: PromptProps & { action?: string, sessionId?: string, role?: string, userId?: string } }) => {
    if (!value?.data?.label) return;

    if (value.data?.action === 'rate') {
      return onSendRate(value.data.label?.toString(), value?.data?.role === 'AI', value?.data?.sessionId ?? '', value?.data?.userId ?? '');
    }
    sendMessage(value.data.label.toString());
  }

  const items = useMemo(() => {
    const items: BubbleDataType[] = messages.map((item) => ({
      key: item?.id ?? item.message?.toString(),
      content: (
        <Message
          message={renderMessage(item.message?.toString() ?? '', item.sender ?? '')}
          attachments={item.attachments}
        />
      ),
      placement: item.sender === 'USER' ? 'end' as const : 'start' as const,
      loading: false,
      role: item.sender,
      footer: item.date && item.sender !== 'SYSTEM' && <StyledTime dateTime={formatDate(item.date)}>{formatTime(item.date)}</StyledTime>
    }));

    return isTyping ? items.concat([{
      key: 'isTyping',
      content: '...' as const,
      loading: true,
      placement: 'start' as const
    }]) : items;
  }, [messages, isTyping]);

  const suggestions: PromptProps[] = useMemo(() => buttons?.map((item) => ({
    label: item.text,
    key: item.text,
    action: item.action,
    sessionId: item.sessionId,
    userId: item.userId,
    role: item.role,
  })), [buttons]);

  const handleSendMessage = (message: string) => {
    sendMessage(message);

    setTimeout(() => {
      ref?.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }


  return (
    <StyledWrapper>
      <StyledContainer
        title={
          <StyledBlock>
            <ChatCircleIcon />
            <div>
              <StyledName>MyCredit</StyledName>
              <StyledInfo>Online</StyledInfo>
            </div>
          </StyledBlock>
        }
        type="inner"
        extra={<Button size="large" ghost icon={<CloseOutlineIcon />} onClick={onClose} />}
        actions={[
          <SenderContainer key="sender" canUpload={!isBot} onSend={handleSendMessage} />
        ]}
      >
        <StyledBubbleList items={items} />
        {suggestions?.length > 0 && (
          <StyledPrompts
            wrap
            items={suggestions}
            onItemClick={handlePressButton}
          />
        )}
        <div ref={ref} />
      </StyledContainer >
      <UserDataContainer
        onCancel={onCancelShareContacts}
        open={shareContacts}
        loading={isSharingContacts}
        saveUserData={setUser}
        userInfo={userInfo}
      />
    </StyledWrapper>
  );
};

export default ChatContainerWrapper;
